import { useState, useCallback } from 'react';
import { migrateHashInfo } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

/**
 * PDF 해시 정보 마이그레이션 모달
 * 기존 매물에 PDF 해시 정보를 추가하는 기능
 */
function PdfMigrationModal({ onClose, onSuccess }) {
  const { role } = useAuth();
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(null);

  const handleFileSelect = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files).filter(file => 
      file.name.match(/\.(pdf)$/i)
    );
    
    if (selectedFiles.length === 0) {
      setError('PDF 파일만 업로드 가능합니다.');
      return;
    }

    setFiles(prev => [...prev, ...selectedFiles]);
    setError(null);
    e.target.value = '';
  }, []);

  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // 드래그 앤 드롭 상태
  const [isDragging, setIsDragging] = useState(false);

  // 드래그 앤 드롭 핸들러
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // 드래그가 영역을 완전히 벗어났을 때만 false로 설정
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const pdfFiles = Array.from(droppedFiles).filter(file => 
        file.name.match(/\.(pdf)$/i)
      );
      
      if (pdfFiles.length === 0) {
        setError('PDF 파일만 업로드 가능합니다.');
        return;
      }

      if (pdfFiles.length !== droppedFiles.length) {
        setError(`${droppedFiles.length - pdfFiles.length}개의 파일이 PDF가 아닙니다.`);
      }

      setFiles(prev => [...prev, ...pdfFiles]);
      setError(null);
    }
  }, []);

  const handleMigrate = useCallback(async () => {
    if (files.length === 0) {
      setError('최소 1개 이상의 PDF 파일을 선택해주세요.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setProgress({ current: 0, total: files.length });
    setResults(null);

    try {
      const result = await migrateHashInfo(files, (current, total) => {
        setProgress({ current, total });
      }, role);

      setResults(result);
      
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      console.error('마이그레이션 오류:', err);
      const errorMessage = err.message || '마이그레이션 중 오류가 발생했습니다.';
      setError(errorMessage.split('\n').join(' '));
    } finally {
      setIsProcessing(false);
    }
  }, [files, onSuccess, role]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-800">PDF 해시 정보 마이그레이션</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              disabled={isProcessing}
              aria-label="닫기"
            >
              ×
            </button>
          </div>

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <p className="text-sm text-blue-800">
              기존 매물에 PDF 해시 정보를 추가합니다. 파일명으로 매물을 찾아 해시 정보를 업데이트합니다.
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
              {error}
            </div>
          )}

          {/* 파일 선택 영역 */}
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium text-gray-700">
              PDF 파일 선택
            </label>
            <div 
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
                isDragging
                  ? 'border-orange-500 bg-orange-50'
                  : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
              } ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                className="hidden"
                id="migration-file-input"
                multiple
                disabled={isProcessing}
              />
              <label
                htmlFor="migration-file-input"
                className={`cursor-pointer ${isProcessing ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                {isDragging ? (
                  <div className="flex flex-col items-center gap-2">
                    <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-sm font-medium text-orange-600">여기에 파일을 놓으세요</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-600">
                        {files.length === 0 ? '파일을 선택하거나 드래그하세요' : `${files.length}개 파일 선택됨`}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">PDF 파일만 가능</p>
                    </div>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* 선택된 파일 목록 */}
          {files.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                선택된 파일 ({files.length}개)
              </h3>
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto">
                <ul className="text-xs text-gray-700 space-y-1">
                  {files.map((file, index) => (
                    <li key={index} className="flex items-center justify-between group">
                      <span className="truncate flex-1 pr-2">{file.name}</span>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-gray-500">{(file.size / 1024).toFixed(1)}KB</span>
                        {!isProcessing && (
                          <button
                            onClick={() => removeFile(index)}
                            className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="파일 제거"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* 진행 상황 */}
          {isProcessing && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded">
              <div className="flex items-center gap-3 mb-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                <p className="text-sm text-blue-800 font-semibold">
                  마이그레이션 진행 중... ({progress.current} / {progress.total})
                </p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* 결과 표시 */}
          {results && (
            <div className="mb-4 space-y-3">
              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800 font-semibold mb-2">
                  마이그레이션 완료
                </p>
                <div className="text-xs text-green-700 space-y-1">
                  <p><span className="font-medium">성공:</span> {results.success || 0}개</p>
                  <p><span className="font-medium">스킵:</span> {results.skipped || 0}개 (이미 마이그레이션됨)</p>
                  <p><span className="font-medium">실패:</span> {results.failed || 0}개</p>
                  <p><span className="font-medium">매칭 안됨:</span> {results.notFound || 0}개</p>
                </div>
              </div>

              {results.details && results.details.length > 0 && (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 max-h-64 overflow-y-auto">
                  <h4 className="text-xs font-semibold text-gray-700 mb-2">상세 결과</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {results.details.map((detail, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          detail.status === 'success' ? 'bg-green-500' :
                          detail.status === 'updated' ? 'bg-blue-500' :
                          detail.status === 'skipped' ? 'bg-yellow-500' :
                          detail.status === 'failed' ? 'bg-red-500' :
                          'bg-gray-400'
                        }`}></span>
                        <span className="truncate flex-1">{detail.fileName}</span>
                        {detail.message && (
                          <span className={`text-[10px] ${
                            detail.status === 'skipped' ? 'text-yellow-600' :
                            detail.status === 'updated' ? 'text-blue-600' :
                            'text-gray-500'
                          }`}>{detail.message}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* 액션 버튼 */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
              disabled={isProcessing}
            >
              {results ? '닫기' : '취소'}
            </button>
            {!results && (
              <button
                onClick={handleMigrate}
                disabled={isProcessing || files.length === 0}
                className="px-4 py-2 bg-orange-500 text-white rounded text-sm font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? '마이그레이션 중...' : '마이그레이션 시작'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PdfMigrationModal;

